import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import Iconify from "../iconify/Iconify";
import { setDialog } from "../../features/dialog/dialogSlice";
import {
  useAddTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useGetTaskQuery,
  useUpdateTaskMutation,
} from "../../features/tasks/tasksApiSlice";
import useAuth from "../../hooks/useAuth";
import { selectCurrentUserData } from "../../features/auth/authSlice";

const STATUS_OPTIONS = [
  { value: "NEU", label: "Neu" },
  { value: "IN_BEARBEITUNG", label: "In Bearbeitung" },
  { value: "IN_PRUEFUNG", label: "In Prüfung" },
  { value: "ERLEDIGT", label: "Erledigt" },
];

const PRIORITY_LABELS = {
  dringend: { label: "Dringend", color: "error" },
  hoch: { label: "Hoch", color: "warning" },
  normal: { label: "Normal", color: "info" },
  niedrig: { label: "Niedrig", color: "success" },
};

export default function TaskDetailDialog({ open, onClose, taskId }) {
  const dispatch = useDispatch();
  const { isMember } = useAuth();
  const userData = useSelector(selectCurrentUserData);
  const currentUserId = userData?.id;

  const { data: task, isLoading, isError, refetch } = useGetTaskQuery(taskId, {
    skip: !taskId,
  });
  const [updateTask] = useUpdateTaskMutation();
  const [addComment] = useAddTaskCommentMutation();
  const [deleteComment] = useDeleteTaskCommentMutation();
  const [commentText, setCommentText] = useState("");

  const assignees = Array.isArray(task?.assignedTo) ? task.assignedTo : [];
  const priority = PRIORITY_LABELS[task?.priority] || PRIORITY_LABELS.normal;

  const canDeleteComment = useMemo(() => {
    return (comment) => {
      if (!comment) return false;
      if (!isMember) return true;
      return comment?.createdBy?._id === currentUserId;
    };
  }, [currentUserId, isMember]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateTask({ id: taskId, status: newStatus }).unwrap();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Status konnte nicht geändert werden." },
        })
      );
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await addComment({ id: taskId, text: commentText.trim() }).unwrap();
      setCommentText("");
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Kommentar konnte nicht gespeichert werden." },
        })
      );
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment({ id: taskId, commentId }).unwrap();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Kommentar konnte nicht gelöscht werden." },
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Aufgabe</Typography>
          <Typography variant="body2" color="text.secondary">
            Details, Status und Kommentare auf einen Blick.
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Typography variant="body2" color="text.secondary">
            Laden…
          </Typography>
        )}
        {isError && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="error">
              Fehler beim Laden.
            </Typography>
            <Button size="small" onClick={refetch}>
              Erneut laden
            </Button>
          </Box>
        )}
        {!isLoading && task && (
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.light, 0.08),
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h6">{task.title}</Typography>
                {task.category && (
                  <Typography variant="body2" color="text.secondary">
                    Kategorie: {task.category}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={priority.label} color={priority.color} size="small" />
              <Chip
                label={`Status: ${
                  STATUS_OPTIONS.find((s) => s.value === task.status)?.label || task.status
                }`}
                size="small"
              />
              {task.dueDate && (
                <Chip
                  label={`Fällig: ${dayjs(task.dueDate).format("DD.MM.YYYY")}`}
                  size="small"
                  color={dayjs(task.dueDate).isBefore(dayjs(), "day") && !task.completedAt ? "error" : "default"}
                />
              )}
            </Stack>

            {task.description && (
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", px: 0.5 }}
              >
                {task.description}
              </Typography>
            )}

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Status ändern
                </Typography>
                <TextField
                  select
                  size="small"
                  value={task.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  fullWidth
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Zugewiesen an
                </Typography>
                {assignees.length > 0 ? (
                  <AvatarGroup max={5} sx={{ "& .MuiAvatar-root": { width: 28, height: 28 } }}>
                    {assignees.map((user) => (
                      <Avatar key={user._id}>
                        {(user.firstName?.[0] || "").toUpperCase()}
                        {(user.lastName?.[0] || "").toUpperCase()}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Keine Zuweisung
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Kommentare</Typography>

              {task.comments?.length > 0 ? (
                <Stack spacing={1}>
                  {task.comments.map((comment) => (
                    <Stack
                      key={comment._id}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        bgcolor: (theme) => alpha(theme.palette.grey[200], 0.3),
                      }}
                    >
                      <Avatar sx={{ width: 28, height: 28 }}>
                        {(comment.createdBy?.firstName?.[0] || "").toUpperCase()}
                        {(comment.createdBy?.lastName?.[0] || "").toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">
                            {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(comment.createdAt).format("DD.MM.YYYY HH:mm")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {comment.text}
                        </Typography>
                      </Box>
                      {canDeleteComment(comment) && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteComment(comment._id)}
                        >
                          <Iconify icon="mdi:trash-can-outline" width={16} />
                        </IconButton>
                      )}
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Kommentare.
                </Typography>
              )}

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  placeholder="Kommentar hinzufügen"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Button variant="contained" onClick={handleAddComment}>
                  Senden
                </Button>
              </Stack>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TaskDetailDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
